import clsx from 'clsx'
import { LocateIcon } from 'lucide-react'
import mapboxgl from 'mapbox-gl'
import { useEffect, useRef, useState } from 'react'

import type { QuoteResult } from 'components/quoting/types'
import { Button, Tooltip, TooltipProvider } from 'slab'
import { formatMoney } from 'utils'
import {
  applyBorderLayersToStates,
  applyFilteredPatternLayersToFilteredStates,
  fetchAndInitializeMapData,
  initializeFilteredPatternToMap,
  initializeMapbox,
  initializeStateClickHandler,
  setColorsForStateLayers,
  setStateLayerOpacities,
  USA_CENTERED_BOUNDS,
} from 'utils/quote_map_utils'

import { COLOR_BUCKETS, useMapHelpers } from '../hooks/quotes/useMapHelpers'

export const Map = ({
  mapboxAccessToken,
  isLoading,
  quoteResults,
  employerEmployeeContribution,
  employerSpouseContribution,
  employerChildContribution,
  onStateClick,
  onStatesSelected,
  filteredStates
}: {
  mapboxAccessToken: string
  isLoading: boolean
  quoteResults: QuoteResult[] | undefined
  employerEmployeeContribution: number
  employerSpouseContribution: number
  employerChildContribution: number
  onStateClick: (state: string) => void
  onStatesSelected: (states: string[]) => void
  filteredStates: string[]
}) => {
  mapboxgl.accessToken = mapboxAccessToken

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const hoveredPolygonIdRef = useRef<number | null>(null)
  const [hoveredStateInfo, setHoveredStateInfo] = useState<{ state: string; allowance: string; employeeCount: number; totalCoveredLives: number; averageCostPerEmployee: string } | null>(null)
  const [hoveredBucketIndex, setHoveredBucketIndex] = useState<number | null>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)

  const {
    cumulativeAllowanceByState,
    stateRankings,
    getColorForState,
    getAllowanceRangeForBucketIndex,
    getStatesInBucketIndex,
    employeeCountByState,
    totalCoveredLivesByState,
  } = useMapHelpers(
    quoteResults,
    employerEmployeeContribution,
    employerSpouseContribution,
    employerChildContribution
  )

  // initialize Mapbox
  useEffect(() => {
    if (!mapContainerRef.current) return
    const map = initializeMapbox(mapContainerRef.current, () => setMapLoaded(true))
    mapRef.current = map
    const onResize = () => {
      if (!mapRef.current) return
      mapRef.current.fitBounds(USA_CENTERED_BOUNDS, { padding: 0, duration: 500 })
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      map.remove()
      mapRef.current = null
      setMapLoaded(false)
    }
  }, [])

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return
    const map = mapRef.current
    // Start zoomed out and centered over the US
    map.setCenter([-98.5, 39.8])
    map.setZoom(1)
    setTimeout(() => {
      map.fitBounds(USA_CENTERED_BOUNDS, { padding: 0, duration: 2000 })
    }, 400)
  }, [mapLoaded])

  // Set up layers and data
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded || !quoteResults) return
    if (!map.getSource('states')) {
      map.addSource('states', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      })
      initializeFilteredPatternToMap(map)
      setStateLayerOpacities(map)
      applyFilteredPatternLayersToFilteredStates(map, filteredStates)
      applyBorderLayersToStates(map, filteredStates)
    }
    initializeStateClickHandler(map, { isLoading, cumulativeAllowanceByState, onStateClick })
    fetchAndInitializeMapData(map, { filteredStates, getColorForState })
  }, [mapLoaded, quoteResults, cumulativeAllowanceByState, getColorForState, isLoading, onStateClick, filteredStates])

  // Update colors and filtered states when they change
  useEffect(() => {
    if (mapRef.current && mapLoaded) {
      setColorsForStateLayers(mapRef.current, filteredStates, getColorForState)
    }
  }, [quoteResults, cumulativeAllowanceByState, getColorForState, mapLoaded, filteredStates])

  // Map hover listeners
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return
    const handleMouseMove = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
      if (!e.features || e.features.length === 0 || !e.features[0]) {
        // No state under cursor: reset hover
        map.setFeatureState(
          { source: 'states', id: hoveredPolygonIdRef.current as number },
          { hover: false }
        )
        hoveredPolygonIdRef.current = null
        setHoveredStateInfo(null)
        setRect(null)
        // Reset cursor to default
        map.getCanvas().style.cursor = 'default'
        return
      }
      const feature = e.features[0]
      if (!feature.properties) return

      setRect(new DOMRect(e.originalEvent.clientX, e.originalEvent.clientY, 0, 0))

      if (hoveredPolygonIdRef.current == null || hoveredPolygonIdRef.current !== feature.id) {
        const stateName = feature.properties.STATE_NAME
        const allowance = cumulativeAllowanceByState[stateName] || 0
        const employeeCount = employeeCountByState[stateName] || 0
        const totalCoveredLives = totalCoveredLivesByState[stateName] || 0
        const averageCostPerEmployee = allowance ? formatMoney(allowance / employeeCount) : 'N/A'
        setHoveredStateInfo({ state: stateName, allowance: formatMoney(allowance), employeeCount, totalCoveredLives, averageCostPerEmployee })
      }

      // Update Mapbox feature-state for hover
      if (hoveredPolygonIdRef.current !== null && hoveredPolygonIdRef.current !== feature.id) {
        map.setFeatureState(
          { source: 'states', id: hoveredPolygonIdRef.current as number },
          { hover: false }
        )
      }
      hoveredPolygonIdRef.current = feature.id as number
      if (hoveredPolygonIdRef.current !== null) {
        map.setFeatureState(
          { source: 'states', id: hoveredPolygonIdRef.current as number },
          { hover: true }
        )
      }
      // Set cursor to pointer when hovering a state
      map.getCanvas().style.cursor = 'pointer'
    }
    const handleMouseLeave = () => {
      if (hoveredPolygonIdRef.current !== null) {
        map.setFeatureState(
          { source: 'states', id: hoveredPolygonIdRef.current as number },
          { hover: false }
        )
      }
      hoveredPolygonIdRef.current = null
      setHoveredStateInfo(null)
      setRect(null)
      map.getCanvas().style.cursor = 'default'
    }
    map.on('mousemove', 'state-fills', handleMouseMove)
    map.on('mouseleave', 'state-fills', handleMouseLeave)
    return () => {
      map.off('mousemove', 'state-fills', handleMouseMove)
      map.off('mouseleave', 'state-fills', handleMouseLeave)
    }
  }, [mapLoaded, cumulativeAllowanceByState])

  // Update fill-opacity based on hoveredBucketIndex
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return
    if (!map.getLayer('state-fills')) return
    if (hoveredBucketIndex === null) {
      // Default opacity logic
      map.setPaintProperty('state-fills', 'fill-opacity', [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        1.0,
        0.75,
      ])
      return
    }
    const statesInBucket = getStatesInBucketIndex(hoveredBucketIndex)
    map.setPaintProperty('state-fills', 'fill-opacity', [
      'case',
      ['in', ['get', 'STATE_NAME'], ['literal', statesInBucket]],
      1.0,
      0.25,
    ])
  }, [hoveredBucketIndex, mapLoaded, stateRankings, getStatesInBucketIndex])

  return (
    <TooltipProvider>
      <div className='relative'>
        <div
          id='map'
          ref={mapContainerRef}
          className={clsx(
            'md:h-96 sm:h-72 rounded-lg',
            '[&_.mapboxgl-canvas-container]:cursor-default'

          )}
          data-testid='map-container'
        />
        {hoveredStateInfo && hoveredStateInfo.employeeCount > 0 && rect && (
          <Tooltip
            content={
              <div className='text-sm space-y-2'>
                <div className='font-medium'>{hoveredStateInfo.state}</div>
                <div className='grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5'>
                  <span className='text-gray-300'>Employees</span>
                  <span className='text-white text-right'>{hoveredStateInfo.employeeCount}</span>
                  <span className='text-gray-300'>Covered lives</span>
                  <span className='text-white text-right'>{hoveredStateInfo.totalCoveredLives}</span>
                  <span className='text-gray-300'>Total cost</span>
                  <span className='text-white text-right'>{hoveredStateInfo.allowance}</span>
                  <span className='text-gray-300'>Avg. per employee</span>
                  <span className='text-white text-right'>{hoveredStateInfo.averageCostPerEmployee}</span>
                </div>
              </div>
            }
            side='top'
            align='center'
            open
            pointerEvents='none'
            virtualAnchor={rect}
          />
        )}
        <div className='absolute top-2 right-2 z-10'>
          <Tooltip content='Re-center map'>
            <Button
              variant='secondary'
              onClick={() => {
                mapRef.current?.fitBounds(USA_CENTERED_BOUNDS, { padding: 0, duration: 800 })
              }}
            >
              <LocateIcon className='w-4 h-4' />
            </Button>
          </Tooltip>
        </div>
        {mapLoaded && (
          <div className='absolute top-0 right-0 bottom-0 flex flex-col items-center justify-center pt-2 z-10'>
            <div className={clsx(
              'flex flex-col gap-2 items-center',
              'bg-white/40 backdrop-blur-sm rounded-lg p-3',
              'shadow-lg border border-gray-100 w-24 mr-2'
            )}
            >
              <div className='text-xs text-gray-600 font-medium text-center'>Total cost per state</div>
              {COLOR_BUCKETS.map((color, index) => {
                const [lower, upper] = getAllowanceRangeForBucketIndex(index)
                const statesInBucket = getStatesInBucketIndex(index)
                const tooltipContent = lower && upper
                  ? `${formatMoney(lower)} - ${formatMoney(upper)}`
                  : upper
                    ? `Up to ${formatMoney(upper)}`
                    : `${formatMoney(lower as number)} and up`
                return (
                  <div
                    key={color}
                    className='p-0.5 group/bucket'
                    onMouseEnter={() => setHoveredBucketIndex(index)}
                    onMouseLeave={() => setHoveredBucketIndex(null)}
                  >
                    <Tooltip key={color} content={tooltipContent} side='right' zIndex={100}>
                      <div
                        className={clsx(
                          'rounded-md border-2 h-5 w-5 cursor-pointer opacity-75',
                          'transition-opacity duration-200',
                          'group-hover/bucket:opacity-100 group-hover/bucket:scale-105',
                          'group-active/bucket:scale-95'
                        )}
                        style={{
                          backgroundColor: color,
                          borderColor: '#4B1377'
                        }}
                        onClick={() => onStatesSelected(statesInBucket)}
                      />
                    </Tooltip>
                  </div>
                )
              }).reverse()}
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}

export default Map
